/**
 * MyRules — Personal Rules Engine.
 *
 * Per-user automations: "When <trigger> [and <conditions>] then <actions>".
 * Rules run server-side and only ever act on the owner's behalf.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getRules, getRuleOptions, createRule, updateRule, deleteRule } from '../api';
import { showToast } from '../utils/toast';
import { formatDateDdMmmYy } from '../utils/format';

// ── Types ───────────────────────────────────────────────────────────────────

type TriggerType =
  | 'TASK_ASSIGNED' | 'TASK_STATUS' | 'INVOICE_EVENT'
  | 'TASK_DUE_SOON' | 'COMPLIANCE_DUE_SOON' | 'SCHEDULED';

interface Condition { field: string; value: string }
interface RuleActions { notify?: boolean; emailMe?: boolean; emailPartner?: boolean; createSubtask?: string | null }
interface Rule {
  id: number;
  name: string;
  triggerType: TriggerType;
  triggerConfig: any;
  conditions: Condition[];
  actions: RuleActions;
  isActive: boolean;
  lastFiredAt?: string | null;
  fireCount: number;
  createdAt: string;
}

type Category = 'tasks' | 'billing' | 'compliance' | 'reminders';

const TRIGGERS: Record<TriggerType, { label: string; icon: string; desc: string; cat: Category; badge: string }> = {
  TASK_ASSIGNED:       { label: 'Task assigned to me',  icon: '📋', desc: 'Fires when a task is assigned to you',         cat: 'tasks',      badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  TASK_DUE_SOON:       { label: 'N days before task due', icon: '⏰', desc: "Fires N days before a task's due date",       cat: 'tasks',      badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  TASK_STATUS:         { label: 'Task status changes',  icon: '🔄', desc: "Fires when a task you're on changes status",   cat: 'tasks',      badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  INVOICE_EVENT:       { label: 'Invoice event',        icon: '🧾', desc: 'Fires when an invoice is Sent / Overdue / Paid', cat: 'billing',  badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  COMPLIANCE_DUE_SOON: { label: 'Compliance due soon',  icon: '📅', desc: 'Fires N days before a compliance deadline',    cat: 'compliance', badge: 'bg-violet-50 text-violet-700 border-violet-200' },
  SCHEDULED:           { label: 'Scheduled (recurring)', icon: '📆', desc: 'Daily / Weekly / Monthly digest',             cat: 'reminders',  badge: 'bg-green-50 text-green-700 border-green-200' },
};

const FREQ_LABEL: Record<string, string> = {
  DAILY: 'Every day',
  WEEKLY_MON: 'Every Monday',
  MONTHLY_1: '1st of each month',
  MONTHLY_LAST: 'Last day of each month',
};

const CONDITION_FIELDS = [
  { value: 'role', label: 'My role on task' },
  { value: 'category', label: 'Task category' },
  { value: 'client', label: 'Client' },
  { value: 'status', label: 'Task status' },
  { value: 'billing', label: 'Billing status' },
];

// ── Trigger summary / chips ──────────────────────────────────────────────────

const describeTrigger = (r: Rule): string => {
  const c = r.triggerConfig || {};
  switch (r.triggerType) {
    case 'TASK_ASSIGNED': return 'Task assigned to me';
    case 'TASK_DUE_SOON': return `${c.daysBefore ?? 7} days before task due`;
    case 'TASK_STATUS': return `Task status → ${c.statusTo || 'any change'}${c.delayDays ? `, ${c.delayDays}d later` : ''}`;
    case 'INVOICE_EVENT': return `Invoice → ${c.invoiceEvent || 'SENT'}`;
    case 'COMPLIANCE_DUE_SOON': return `${c.daysBefore ?? 7} days before compliance due`;
    case 'SCHEDULED': return FREQ_LABEL[c.frequency] || 'Scheduled';
    default: return '';
  }
};

const actionChips = (a: RuleActions): string[] => {
  const out: string[] = [];
  if (a.notify) out.push('🔔 Notify me');
  if (a.emailMe) out.push('📧 Email me');
  if (a.emailPartner) out.push('📨 Email partner');
  if (a.createSubtask) out.push(`➕ Sub-task "${a.createSubtask}"`);
  return out;
};

const condChip = (c: Condition): string => {
  const f = CONDITION_FIELDS.find((x) => x.value === c.field)?.label || c.field;
  return `${f}: ${c.value}`;
};

// ── Empty form ───────────────────────────────────────────────────────────────

const emptyDraft = () => ({
  id: null as number | null,
  name: '',
  triggerType: null as TriggerType | null,
  triggerConfig: {} as any,
  conditions: [] as Condition[],
  actions: { notify: false, emailMe: false, emailPartner: false, createSubtask: null } as RuleActions,
});

const defaultConfigFor = (t: TriggerType): any => {
  switch (t) {
    case 'TASK_DUE_SOON':
    case 'COMPLIANCE_DUE_SOON': return { daysBefore: 7 };
    case 'TASK_STATUS': return { statusTo: 'CLOSED', delayDays: 0 };
    case 'INVOICE_EVENT': return { invoiceEvent: 'OVERDUE' };
    case 'SCHEDULED': return { frequency: 'WEEKLY_MON' };
    default: return {};
  }
};

// ── Component ────────────────────────────────────────────────────────────────

const MyRules: React.FC = () => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<{ categories: string[]; clients: string[] }>({ categories: [], clients: [] });
  const [tab, setTab] = useState<'all' | Category>('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRules();
      setRules(res.data);
    } catch {
      showToast('Failed to load rules', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getRuleOptions().then((r) => setOptions(r.data)).catch(() => {}); }, []);

  const activeCount = useMemo(() => rules.filter((r) => r.isActive).length, [rules]);
  const firedThisMonth = useMemo(() => rules.reduce((s, r) => s + (r.fireCount || 0), 0), [rules]);
  const visible = useMemo(
    () => (tab === 'all' ? rules : rules.filter((r) => TRIGGERS[r.triggerType]?.cat === tab)),
    [rules, tab],
  );

  // ── Builder helpers ──
  const openCreate = () => { setDraft(emptyDraft()); setStep(1); setModalOpen(true); };
  const openEdit = (r: Rule) => {
    setDraft({
      id: r.id, name: r.name, triggerType: r.triggerType,
      triggerConfig: r.triggerConfig || {}, conditions: r.conditions || [],
      actions: { notify: false, emailMe: false, emailPartner: false, createSubtask: null, ...r.actions },
    });
    setStep(1); setModalOpen(true);
  };

  const pickTrigger = (t: TriggerType) =>
    setDraft((d) => ({ ...d, triggerType: t, triggerConfig: defaultConfigFor(t) }));

  const setConfig = (patch: any) => setDraft((d) => ({ ...d, triggerConfig: { ...d.triggerConfig, ...patch } }));

  const addCond = () => setDraft((d) => ({ ...d, conditions: [...d.conditions, { field: '', value: '' }] }));
  const setCond = (i: number, patch: Partial<Condition>) =>
    setDraft((d) => ({ ...d, conditions: d.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const removeCond = (i: number) =>
    setDraft((d) => ({ ...d, conditions: d.conditions.filter((_, idx) => idx !== i) }));

  const condValues = (field: string): string[] => {
    switch (field) {
      case 'role': return ['Partner', 'Manager', 'Assigned staff', 'Any'];
      case 'category': return options.categories;
      case 'client': return options.clients;
      case 'status': return ['OPEN', 'CLOSED'];
      case 'billing': return ['BILLED', 'UNBILLED'];
      default: return [];
    }
  };

  const previewActions = actionChips(draft.actions);

  const save = async () => {
    if (!draft.triggerType) { showToast('Pick a trigger', 'error'); return; }
    if (!draft.name.trim()) { showToast('Give the rule a name', 'error'); return; }
    const hasAction = draft.actions.notify || draft.actions.emailMe || draft.actions.emailPartner || draft.actions.createSubtask;
    if (!hasAction) { showToast('Pick at least one action', 'error'); return; }

    const payload = {
      name: draft.name.trim(),
      triggerType: draft.triggerType,
      triggerConfig: draft.triggerConfig,
      conditions: draft.conditions.filter((c) => c.field && c.value),
      actions: draft.actions,
    };
    setSaving(true);
    try {
      if (draft.id) await updateRule(draft.id, payload);
      else await createRule(payload);
      showToast(draft.id ? 'Rule updated' : 'Rule created', 'success');
      setModalOpen(false);
      load();
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Error saving rule', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (r: Rule) => {
    // optimistic
    setRules((rs) => rs.map((x) => (x.id === r.id ? { ...x, isActive: !x.isActive } : x)));
    try {
      await updateRule(r.id, { isActive: !r.isActive });
    } catch {
      setRules((rs) => rs.map((x) => (x.id === r.id ? { ...x, isActive: r.isActive } : x)));
      showToast('Failed to update rule', 'error');
    }
  };

  const remove = async (r: Rule) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try {
      await deleteRule(r.id);
      setRules((rs) => rs.filter((x) => x.id !== r.id));
    } catch {
      showToast('Failed to delete rule', 'error');
    }
  };

  // ── Render ──
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Rules</h2>
          <p className="text-sm text-gray-500 mt-1">Personal automations — they run only for you and never affect other users.</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>⚡ + New Rule</button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card p-4"><div className="text-2xl font-bold text-gray-900">{rules.length}</div><div className="text-xs text-gray-500 mt-0.5">Total rules</div></div>
        <div className="card p-4"><div className="text-2xl font-bold text-blue-600">{activeCount}</div><div className="text-xs text-gray-500 mt-0.5">Active</div></div>
        <div className="card p-4"><div className="text-2xl font-bold text-emerald-600">{firedThisMonth}</div><div className="text-xs text-gray-500 mt-0.5">Times fired (total)</div></div>
        <div className="card p-4"><div className="text-2xl font-bold text-violet-600">{rules.length}/25</div><div className="text-xs text-gray-500 mt-0.5">Rules used</div></div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {(['all', 'tasks', 'billing', 'compliance', 'reminders'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t === 'all' ? `All (${rules.length})` : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        {loading ? (
          <p className="text-gray-500 text-sm p-6">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            {rules.length === 0 ? 'No rules yet. Click "+ New Rule" to create your first automation.' : 'No rules in this category.'}
          </div>
        ) : (
          visible.map((r) => (
            <div key={r.id} className={`border-b border-gray-100 last:border-0 px-5 py-4 flex items-start gap-3 ${r.isActive ? '' : 'opacity-50'}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-bold text-gray-900 text-sm">{r.name}</span>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${TRIGGERS[r.triggerType]?.badge}`}>
                    {TRIGGERS[r.triggerType]?.icon} {TRIGGERS[r.triggerType]?.cat}
                  </span>
                  {!r.isActive && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full border bg-gray-100 text-gray-500 border-gray-200">⏸ Paused</span>}
                </div>
                <div className="flex items-center flex-wrap gap-1.5 text-xs text-gray-600 mb-2">
                  <span>{describeTrigger(r)}</span>
                  {(r.conditions || []).length > 0 && <span className="text-gray-300">›</span>}
                  {(r.conditions || []).map((c, i) => (
                    <span key={i} className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">{condChip(c)}</span>
                  ))}
                  <span className="text-gray-300">›</span>
                  {actionChips(r.actions).map((a, i) => (
                    <span key={i} className="px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-100">{a}</span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                    {r.lastFiredAt ? `Last fired: ${formatDateDdMmmYy(r.lastFiredAt)}` : 'Never fired yet'}
                  </span>
                  <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Fired {r.fireCount}×</span>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 pt-0.5">
                <label className="relative inline-block w-9 h-5 cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={r.isActive} onChange={() => toggle(r)} />
                  <span className="absolute inset-0 rounded-full bg-gray-300 peer-checked:bg-blue-600 transition-colors" />
                  <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
                </label>
                <button className="btn-secondary text-xs px-2.5 py-1" onClick={() => openEdit(r)}>Edit</button>
                <button className="text-red-600 hover:text-red-800 text-xs font-medium border border-red-200 rounded-lg px-2.5 py-1" onClick={() => remove(r)}>Del</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Builder modal ── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-900">⚡ Rule Builder</h3>
                <p className="text-xs text-gray-500 mt-0.5">Build your automation in 3 steps</p>
              </div>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>

            {/* Step indicators */}
            <div className="flex items-center px-6 py-4">
              {[1, 2, 3].map((n, i) => (
                <React.Fragment key={n}>
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= n ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'}`}>{n}</div>
                    <span className={`text-sm ${step >= n ? 'text-blue-700 font-semibold' : 'text-gray-400'}`}>{n === 1 ? 'Trigger' : n === 2 ? 'Conditions' : 'Action & Save'}</span>
                  </div>
                  {i < 2 && <div className="flex-1 h-0.5 bg-gray-200 mx-3" />}
                </React.Fragment>
              ))}
            </div>

            <div className="px-6 pb-6">
              {/* STEP 1 */}
              {step === 1 && (
                <>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">When does this rule fire?</h4>
                    <div className="grid grid-cols-2 gap-2.5">
                      {(Object.keys(TRIGGERS) as TriggerType[]).map((t) => (
                        <button key={t} onClick={() => pickTrigger(t)}
                          className={`text-left border-2 rounded-xl p-3 transition-colors ${draft.triggerType === t ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
                          <div className="text-lg mb-1">{TRIGGERS[t].icon}</div>
                          <div className="text-xs font-bold text-gray-900">{TRIGGERS[t].label}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">{TRIGGERS[t].desc}</div>
                        </button>
                      ))}
                    </div>

                    {/* Sub-config */}
                    {draft.triggerType && (
                      <div className="mt-3 p-3 bg-white border border-blue-100 rounded-xl">
                        {(draft.triggerType === 'TASK_DUE_SOON' || draft.triggerType === 'COMPLIANCE_DUE_SOON') && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-700">Fire</span>
                            <input type="number" min={1} max={60} className="input-field w-20 text-sm"
                              value={draft.triggerConfig.daysBefore ?? 7}
                              onChange={(e) => setConfig({ daysBefore: Number(e.target.value) })} />
                            <span className="text-xs text-gray-500">days before due date</span>
                          </div>
                        )}
                        {draft.triggerType === 'TASK_STATUS' && (
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-700">When status →</span>
                              <select className="input-field w-32 text-sm" value={draft.triggerConfig.statusTo || 'CLOSED'}
                                onChange={(e) => setConfig({ statusTo: e.target.value })}>
                                <option value="ANY">Any change</option>
                                <option value="OPEN">OPEN</option>
                                <option value="CLOSED">CLOSED</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-700">Wait</span>
                              <input type="number" min={0} max={60} className="input-field w-20 text-sm"
                                value={draft.triggerConfig.delayDays ?? 0}
                                onChange={(e) => setConfig({ delayDays: Number(e.target.value) })} />
                              <span className="text-xs text-gray-500">days before acting</span>
                            </div>
                          </div>
                        )}
                        {draft.triggerType === 'INVOICE_EVENT' && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-700">Invoice event</span>
                            <select className="input-field w-32 text-sm" value={draft.triggerConfig.invoiceEvent || 'OVERDUE'}
                              onChange={(e) => setConfig({ invoiceEvent: e.target.value })}>
                              <option value="SENT">Sent</option>
                              <option value="OVERDUE">Overdue</option>
                              <option value="PAID">Paid</option>
                            </select>
                          </div>
                        )}
                        {draft.triggerType === 'SCHEDULED' && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-700">Frequency</span>
                            <select className="input-field w-56 text-sm" value={draft.triggerConfig.frequency || 'WEEKLY_MON'}
                              onChange={(e) => setConfig({ frequency: e.target.value })}>
                              <option value="DAILY">Every day</option>
                              <option value="WEEKLY_MON">Every Monday</option>
                              <option value="MONTHLY_1">1st of each month</option>
                              <option value="MONTHLY_LAST">Last day of each month</option>
                            </select>
                          </div>
                        )}
                        {draft.triggerType === 'TASK_ASSIGNED' && (
                          <p className="text-xs text-gray-500">Fires the moment a task is assigned to you. No extra settings.</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button className="btn-primary disabled:opacity-50" disabled={!draft.triggerType} onClick={() => setStep(2)}>Next: Conditions →</button>
                  </div>
                </>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">Only fire if… <span className="font-normal text-gray-500 text-xs">(optional)</span></h4>
                      <button className="text-xs text-blue-600 font-semibold" onClick={addCond}>+ Add filter</button>
                    </div>
                    {draft.conditions.length === 0 && <p className="text-xs text-gray-400">No filters — fires for every matching event.</p>}
                    <div className="space-y-2">
                      {draft.conditions.map((c, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <select className="input-field flex-1 text-sm" value={c.field}
                            onChange={(e) => setCond(i, { field: e.target.value, value: '' })}>
                            <option value="">— Choose filter —</option>
                            {CONDITION_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                          </select>
                          <select className="input-field flex-1 text-sm" value={c.value} disabled={!c.field}
                            onChange={(e) => setCond(i, { value: e.target.value })}>
                            <option value="">— Value —</option>
                            {condValues(c.field).map((v) => <option key={v} value={v}>{v}</option>)}
                          </select>
                          <button className="text-gray-400 hover:text-gray-600 text-lg" onClick={() => removeCond(i)}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <button className="btn-secondary" onClick={() => setStep(1)}>← Back</button>
                    <button className="btn-primary" onClick={() => setStep(3)}>Next: Action →</button>
                  </div>
                </>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                <>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Then do… <span className="font-normal text-gray-500 text-xs">(pick one or more)</span></h4>
                    <div className="space-y-2">
                      <label className="flex items-start gap-2.5 p-2.5 border border-gray-200 rounded-xl cursor-pointer hover:bg-white">
                        <input type="checkbox" className="mt-0.5 w-4 h-4" checked={!!draft.actions.notify}
                          onChange={(e) => setDraft((d) => ({ ...d, actions: { ...d.actions, notify: e.target.checked } }))} />
                        <div><div className="text-sm font-semibold text-gray-900">🔔 In-app notification</div><div className="text-[11px] text-gray-500">Show a notification badge in my inbox</div></div>
                      </label>
                      <label className="flex items-start gap-2.5 p-2.5 border border-gray-200 rounded-xl cursor-pointer hover:bg-white">
                        <input type="checkbox" className="mt-0.5 w-4 h-4" checked={!!draft.actions.emailMe}
                          onChange={(e) => setDraft((d) => ({ ...d, actions: { ...d.actions, emailMe: e.target.checked } }))} />
                        <div><div className="text-sm font-semibold text-gray-900">📧 Email me</div><div className="text-[11px] text-gray-500">Send a summary to my registered email</div></div>
                      </label>
                      <label className="flex items-start gap-2.5 p-2.5 border border-gray-200 rounded-xl cursor-pointer hover:bg-white">
                        <input type="checkbox" className="mt-0.5 w-4 h-4" checked={!!draft.actions.emailPartner}
                          onChange={(e) => setDraft((d) => ({ ...d, actions: { ...d.actions, emailPartner: e.target.checked } }))} />
                        <div><div className="text-sm font-semibold text-gray-900">📨 Email the task's partner</div><div className="text-[11px] text-gray-500">Notify the task partner when the rule fires</div></div>
                      </label>
                      <div className="p-2.5 border border-gray-200 rounded-xl">
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input type="checkbox" className="mt-0.5 w-4 h-4" checked={draft.actions.createSubtask != null}
                            onChange={(e) => setDraft((d) => ({ ...d, actions: { ...d.actions, createSubtask: e.target.checked ? (d.actions.createSubtask || 'Follow up') : null } }))} />
                          <div>
                            <div className="text-sm font-semibold text-gray-900">➕ Auto-create a sub-task</div>
                            <div className="text-[11px] text-gray-500">Create a named sub-task on the triggered task</div>
                          </div>
                        </label>
                        {draft.actions.createSubtask != null && (
                          <input className="input-field mt-2 text-sm" placeholder='Sub-task name, e.g. "Initial review"'
                            value={draft.actions.createSubtask}
                            onChange={(e) => setDraft((d) => ({ ...d, actions: { ...d.actions, createSubtask: e.target.value } }))} />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-sm font-semibold text-gray-700 block mb-1.5">Rule name *</label>
                    <input className="input-field" placeholder='e.g. "GST reminder — 7 days before"' value={draft.name}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 mb-4">
                    <div className="text-[10px] font-bold text-blue-800 uppercase tracking-wide mb-1.5">Preview</div>
                    <div className="text-sm text-blue-900 leading-relaxed">
                      <strong>{draft.name || '(unnamed rule)'}</strong><br />
                      <span className="text-blue-500">⚡ When:</span> {draft.triggerType ? describeTrigger({ ...(draft as any) } as Rule) : '—'}<br />
                      {draft.conditions.filter((c) => c.field && c.value).length > 0 && (
                        <><span className="text-blue-500">🔎 If:</span> {draft.conditions.filter((c) => c.field && c.value).map(condChip).join(', ')}<br /></>
                      )}
                      <span className="text-blue-500">→ Then:</span> {previewActions.length ? previewActions.join(', ') : <em className="text-blue-400">no action selected yet</em>}
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <button className="btn-secondary" onClick={() => setStep(2)}>← Back</button>
                    <button className="btn-primary bg-green-600 hover:bg-green-700 disabled:opacity-50" disabled={saving} onClick={save}>
                      {saving ? 'Saving…' : draft.id ? '✓ Update Rule' : '✓ Save Rule'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRules;
